import { motion } from "framer-motion";
import { ShieldCheck, HandCoins, Languages, MapPin, Wifi, CreditCard, Sparkles } from "lucide-react";
import { IMAGES } from "@/data/content";
import { useLang } from "@/i18n";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { CTAButton, SectionLabel } from "./ui";
import { AuroraBackground } from "./Aurora";

const TRUST_ICONS = [ShieldCheck, Sparkles, Languages, MapPin];

export const Hero = () => {
  const { t } = useLang();
  const h = t.hero;
  return (
    <section id="home" data-testid="hero-section" className="relative flex items-center pt-24 lg:pt-32 pb-12 lg:pb-20 overflow-hidden">
      <AuroraBackground opacity={0.5} />
      <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center relative w-full">
        {/* Copy */}
        <div>
          <SectionLabel>{h.label}</SectionLabel>
          <motion.h1
            key={h.lines[0]}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-4 font-display font-bold text-navy-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight"
          >
            {h.lines.map((l, i) => (
              <span key={i} className="block">{i === 1 ? <span className="italic gradient-text">{l}</span> : l}</span>
            ))}
          </motion.h1>
          <p className="mt-5 text-base sm:text-lg text-charcoal-700 leading-relaxed max-w-xl">{h.sub}</p>

          {/* Mobile device image — visible right after headline+text */}
          <div className="mt-6 lg:hidden relative rounded-2xl overflow-hidden shadow-xl ring-1 ring-navy-900/5">
            <img src={IMAGES.device} alt={h.imgAlt} className="w-full h-52 sm:h-64 object-cover" fetchpriority="high" />
            <span className="absolute bottom-3 left-3 inline-flex items-center gap-2 rounded-full bg-navy-900/85 backdrop-blur px-3 py-1.5 text-xs font-semibold text-white">
              <Wifi size={13} className="text-gold-400" aria-hidden /> {h.contactless} · {h.tap}
            </span>
          </div>

          <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3">
            <CTAButton
              href="#contact"
              data-testid="hero-primary-cta"
              onClick={() => trackEvent(EVENTS.PRIMARY_CTA, { placement: "hero" })}
              className="w-full sm:w-auto justify-center min-h-[48px]"
            >
              <HandCoins size={20} aria-hidden /> {h.cta1}
            </CTAButton>
            <CTAButton
              href="#solutions"
              variant="secondary"
              data-testid="hero-secondary-cta"
              onClick={() => trackEvent(EVENTS.SECONDARY_CTA, { placement: "hero" })}
              className="w-full sm:w-auto justify-center min-h-[48px]"
            >
              {h.cta2}
            </CTAButton>
          </div>

          <ul className="mt-7 grid grid-cols-2 sm:flex sm:flex-wrap gap-x-6 gap-y-2.5 text-sm text-charcoal-700/80" data-testid="hero-trust-indicators">
            {h.trust.map((item, i) => {
              const Icon = TRUST_ICONS[i];
              return (
                <li key={item} className="flex items-center gap-2">
                  <Icon size={16} className="text-gold-600 shrink-0" aria-hidden /> {item}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Desktop device image */}
        <motion.div
          initial={{ opacity: 0, scale: 1.03 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative hidden lg:block"
        >
          <div className="relative rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-navy-900/5">
            <img src={IMAGES.device} alt={h.imgAlt} className="w-full h-[540px] object-cover" fetchpriority="high" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-900/35 via-transparent to-transparent" />
          </div>
          <div className="absolute -bottom-5 left-8 flex items-center gap-3 rounded-2xl bg-white text-navy-900 shadow-xl px-5 py-4 border border-navy-100">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-navy-900 text-gold-400">
              <Wifi size={20} aria-hidden />
            </span>
            <div>
              <p className="text-xs uppercase tracking-wide text-charcoal-700/60 font-semibold">{h.contactless}</p>
              <p className="font-semibold flex items-center gap-1.5"><CreditCard size={16} aria-hidden /> {h.tap}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
